import { FC, useContext, useEffect, useState, ReactNode, useRef, useMemo, useCallback } from 'react';
import { AiChatMessage, AiChatMessageType, AiChatResponse } from "@app/projects/network-assessment/provider-hub/interfaces";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import ChatbotService from "@app/projects/network-assessment/provider-hub/ai-assistant/chatbotService";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { AIChatTableChart } from "@app/projects/network-assessment/provider-hub/ai-assistant/AIChatTableChart";
import { ModalLink } from "@app/shared/Modals";
import {
  userCanAccessAIChatPanel
} from "@app/projects/network-assessment/provider-hub/ai-assistant/aiChatPanelAuthorizationHelper";
import {
  AIChatPanelProviderLink
} from "@app/projects/network-assessment/provider-hub/ai-assistant/AIChatPanelProviderLink";
import {
  useCellMarkdownWithCompleteProviderLinks
} from "@app/projects/network-assessment/provider-hub/ai-assistant/hooks/useCellMarkdownWithCompleteProviderLinks";
import { AiResponseReviewService } from "@app/operations/ai-response-review/aiResponseReviewService";
import { AiResponseReviewRating } from "@app/operations/ai-response-review/enums";
import { toast } from "react-toastify";
import { AIChatLoadingStatuses } from "@app/projects/network-assessment/provider-hub/ai-assistant/AIChatLoadingStatuses";
import AIChatBarChart from "@app/projects/network-assessment/provider-hub/ai-assistant/AIChatBarChart";
import AIChatPieChart from "@app/projects/network-assessment/provider-hub/ai-assistant/AIChatPieChart";
import { buildDisplayContextPayload } from "@app/projects/network-assessment/provider-hub/ai-assistant/aiChatChartHelper";
import {
  buildAppliedFiltersSystemMessage,
  buildProviderClaimsInsightsTabSystemMessage,
  buildSummariesTabSystemMessage
} from "@app/projects/network-assessment/provider-hub/ai-assistant/aiChatContextHelper";
import { ProjectModule } from "@app/projects/literals";
import Collapsible from "@app/shared/Collapsible";
import cx from "classnames";
import { useRethrowToErrorBoundary } from "@app/shared/useRethrowToErrorBoundary";
import { CountyProjectContext } from '@app/projects/CountyProjectContext';
import { UserContext } from '@app/user/UserContext';
import { QESButton } from '@app/shared/qes-standards/Button';
import { QESTextArea } from '@app/shared/qes-standards/forms/TextArea';
import { QESTextInput } from '@app/shared/qes-standards/forms/TextInput';
import { useRequestLimits } from "@app/projects/network-assessment/provider-hub/ai-assistant/hooks/useRequestLimits";
import { QuinnLimitRequestInfo } from "@app/projects/network-assessment/provider-hub/ai-assistant/QuinnLimitRequestInfo";
import { findLandedMessage, waitForAnswer } from "@app/projects/network-assessment/provider-hub/ai-assistant/waitForAnswer";
import { useFullScreenScrollLock } from "@app/projects/network-assessment/provider-hub/ai-assistant/hooks/useFullScreenScrollLock";

interface AIChatPanelProps {
  projectRunConfigurationId: number;
  /**
   * Float over the page instead of docking as a rail beside it. Resolved by `isQuinnFlyoutLayout`
   * at the call site, which also decides whether to apply the page's side-by-side classes — the two
   * have to agree, so the decision is made once there rather than re-derived here.
   */
  isFlyout?: boolean;
  /**
   * Drives the slide transition when `isFlyout`. Owned by QuinnFlyout, which sequences it against
   * mount and unmount.
   */
  isVisible?: boolean;
  /** The project tab the user is on, so Quinn can be told about tabs whose numbers it can't reproduce. */
  activeProjectModule?: ProjectModule;
  /**
   * Provider Claims Insights is a Provider Hub subpage rather than its own module, so it can't be read off
   * `activeProjectModule`; Quinn is told what that tab means by "Provider Status".
   */
  isOnProviderClaimsInsightsTab?: boolean;
}

const feedbackTips = `
If you're having trouble, you might try the following:
- Clearing your chat history and then asking again
- Being as specific as possible
  - Clearly state the goal you want.
    - Ex: "Please give me 10 primary care providers that serve the most members."
  - Use Constraint-Based Prompts
    - Ex: "Only the providers that close a gap..."
- Framing your question in terms of specific providers
  `;

const AIChatPanel: FC<AIChatPanelProps> = ({ projectRunConfigurationId, isFlyout, isVisible, activeProjectModule, isOnProviderClaimsInsightsTab }) => {
  const chatbotService = useMemo(() => new ChatbotService(), []);
  const aiResponseReviewService = useMemo(() => new AiResponseReviewService(), []);
  const featureFlagContext = useContext(FeatureFlagContext);
  const projectContext = useContext(CountyProjectContext);
  const getCellMarkdownWithCompleteProviderLinks = useCellMarkdownWithCompleteProviderLinks();
  const { processRequestLimits, limitReached, limitsLoading, showLimitRequestInfo, warningText } = useRequestLimits();
  const userContext = useContext(UserContext);
  const [messages, setMessages] = useState<AiChatMessage[]>([]);
  const [prompt, setPrompt] = useState<string>('');
  const [threadId, setThreadId] = useState<string>('');
  const [responseFeedbackSelectionById, setResponseFeedbackSelectionById] = useState<Record<number, AiResponseReviewRating | undefined>>({});
  const [selectedResponseId, setSelectedResponseId] = useState<number | undefined>(undefined);
  const [feedback, setFeedback] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Tracks the thread ID sent to the server synchronously so AIChatLoadingStatuses can filter
  // SignalR progress events correctly even before the setThreadId state update takes effect.
  const effectiveThreadIdRef = useRef<string>('');
  // Responses that already have negative feedback on screen, so re-rating (e.g. Bad -> Good -> Bad)
  // never stacks a second apology under the same response.
  const apologizedResponseIdsRef = useRef<Set<number>>(new Set());
  const { setUnhandledError, rethrowIfUnhandled } = useRethrowToErrorBoundary();
  useFullScreenScrollLock(isFullScreen);

  const getStorageKey = (runConfigurationId: number) => `ai-thread-${runConfigurationId}`;

  /**
   * Puts a whole thread fetched from the server on screen. Used both when rehydrating on reopen and
   * when the answer had to be read back because the POST never delivered it — in both cases the
   * server returns the entire thread, so the message list is replaced rather than appended to.
   */
  const applyConversation = (conversation: AiChatResponse) => {
    const storedResponseFeedbackSelection: Record<number, AiResponseReviewRating> = {};
    const alreadyRatedBadResponseIds = new Set<number>();
    for (const message of conversation.messages) {
      if (!!message.userRating) {
        storedResponseFeedbackSelection[message.responseId] = message.userRating;
      }
      if (message.userRating === AiResponseReviewRating.Bad) {
        alreadyRatedBadResponseIds.add(message.responseId);
      }
    }
    setResponseFeedbackSelectionById(storedResponseFeedbackSelection);
    apologizedResponseIdsRef.current = alreadyRatedBadResponseIds;
    setMessages(conversation.messages);
  };

  useEffect(() => {
    const sessionThreadId = sessionStorage.getItem(getStorageKey(projectRunConfigurationId));
    if (sessionThreadId) {
      setThreadId(sessionThreadId);

      const loadConversation = async () => {
        try {
          applyConversation(await chatbotService.getConversation(sessionThreadId));
        } catch {
          setMessages([]);
          setUnhandledError();
        }
      };
      loadConversation();
    } else {
      setThreadId('');
      setMessages([]);
    }
  }, [projectRunConfigurationId]);

  useEffect(() => {
    const key = getStorageKey(projectRunConfigurationId);
    const hasAccess = userCanAccessAIChatPanel(featureFlagContext.enabledFeatureFlags, userContext.roles);
    if (!hasAccess) {
      sessionStorage.removeItem(key);
      setThreadId('');
      setMessages([]);
    }
  }, [featureFlagContext.enabledFeatureFlags, userContext.roles, projectRunConfigurationId])

  const scrollToBottom = () => {
    const container = document.querySelector('.ai-chat-panel__messages');
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  };

  // The flyout stays mounted while hidden, so focus on every reopen rather than only on mount.
  useEffect(() => {
    if ((!isFlyout || isVisible) && !!inputRef.current) {
      inputRef.current.focus();
    }
  }, [isFlyout, isVisible]);

  // Hiding the flyout used to unmount the panel, which is what dropped full screen (and its page
  // scroll lock). Now that it stays mounted, drop it explicitly.
  useEffect(() => {
    if (isFlyout && !isVisible) {
      setIsFullScreen(false);
    }
  }, [isFlyout, isVisible]);

  useEffect(() => {
    scrollToBottom();
  }, [messages])

  const handleSubmit = async (promptOverride?: string) => {
    const promptToSubmit = !!prompt ? prompt : promptOverride;
    if (!promptToSubmit || limitReached || limitsLoading) {
      return;
    }
    setLoading(true);
    // Mint the thread id up front for a brand-new conversation so the progress-status handler can
    // filter updates by it immediately (status events arrive before the HTTP response returns the
    // id). The server honours a client-supplied thread id via Guid.TryParse.
    const activeThreadId = threadId || crypto.randomUUID();
    if (activeThreadId !== threadId) {
      setThreadId(activeThreadId);
    }
    effectiveThreadIdRef.current = activeThreadId;
    // Snapshot the highest response id we already know about. ResponseId is a SQL identity, so anything
    // greater than this in a later read-back is necessarily the answer to the prompt we are about to send.
    const sinceResponseId = messages.reduce((max, message) => Math.max(max, message.responseId ?? 0), 0);
    const messagesToSend = buildDisplayContextPayload(messages);
    try {
      // Pushed ahead of the provider/contract-entity markers: the backend only inspects the message
      // immediately before the prompt for the provider-profile marker, so this must not displace it.
      // Mirrors CountyProjectContainer's county chip validation: fall back to the compare-to run configuration.
      const appliedFiltersMessage = buildAppliedFiltersSystemMessage(projectContext.filters ?? [], countyRegionCode =>
        (projectContext.runConfiguration?.getCounty(countyRegionCode)
          ?? projectContext.compareToRunConfiguration?.getCounty(countyRegionCode))?.canonicalRegionSetId);
      if (appliedFiltersMessage) {
        messagesToSend.push(appliedFiltersMessage);
      }

      // Same placement constraint as the applied-filters message above.
      const summariesTabMessage = buildSummariesTabSystemMessage(activeProjectModule);
      if (summariesTabMessage) {
        messagesToSend.push(summariesTabMessage);
      }

      // Same placement constraint as the applied-filters message above.
      const providerClaimsInsightsTabMessage = buildProviderClaimsInsightsTabSystemMessage(isOnProviderClaimsInsightsTab);
      if (providerClaimsInsightsTabMessage) {
        messagesToSend.push(providerClaimsInsightsTabMessage);
      }

      if (!!projectContext.virtualAssistantContext?.servicingProviderKey) {
        const nameAvailable = !!projectContext.virtualAssistantContext.servicingProviderName
        messagesToSend.push({
          content: [`The next user message was sent while the user was viewing the provider${
            nameAvailable ? ` named ${projectContext.virtualAssistantContext.servicingProviderName}` : ''
          } with SERVICING_PROVIDER_KEY ${projectContext.virtualAssistantContext.servicingProviderKey}`],
          messageType: AiChatMessageType.System,
          timestamp: new Date()
        });
      }

      if (!!projectContext.virtualAssistantContext?.contractEntityId) {
        const nameAvailable = !!projectContext.virtualAssistantContext.contractEntityName
        messagesToSend.push({
          content: [`The next user message was sent while the user was viewing the contract entity${
            nameAvailable ? ` named ${projectContext.virtualAssistantContext.contractEntityName}` : ''
          } with CONTRACT_ENTITY_ID ${projectContext.virtualAssistantContext.contractEntityId}`],
          messageType: AiChatMessageType.System,
          timestamp: new Date()
        });
      }
      messagesToSend.push({
        content: [promptToSubmit],
        messageType: AiChatMessageType.UserPrompt,
        timestamp: new Date()
      });
      setMessages(messagesToSend);
      setPrompt('');

      const post = chatbotService.sendConversation({
        messages: messagesToSend,
        projectRunConfigurationId,
        threadId: activeThreadId
      });
      const outcome = await waitForAnswer(chatbotService, post, activeThreadId, sinceResponseId);

      if (outcome.kind === 'lost') {
        // Neither the POST nor the read-back produced an answer; fall through to the existing failure path.
        throw new Error('Quinn did not deliver an answer for this turn.');
      }

      if (outcome.kind === 'recovered') {
        const { conversation } = outcome;
        applyConversation(conversation);
        setThreadId(conversation.threadId);
        sessionStorage.setItem(getStorageKey(projectRunConfigurationId), conversation.threadId);

        // The read-back returns the whole thread, so hand the limits hook only the message this turn
        // produced — it reads the exclusion flag off messages[0].
        const landedMessage = findLandedMessage(conversation.messages, sinceResponseId);
        if (!!landedMessage) {
          processRequestLimits({ messages: [landedMessage], threadId: conversation.threadId });
        }
        return;
      }

      const result = outcome.response;
      setMessages([...messagesToSend, ...result.messages]);

      if (result.threadId) {
        setThreadId(result.threadId);
        sessionStorage.setItem(getStorageKey(projectRunConfigurationId), result.threadId);
      }

      processRequestLimits(result);
    } catch {
      setMessages([...messagesToSend, {
        content: ['Something went wrong. Please try again.'],
        messageType: AiChatMessageType.ResponseText,
        timestamp: new Date(),
      }]);
      setUnhandledError();
    } finally {
      setLoading(false);
    }
  }

  const handleClose = () => {
    projectContext.setVirtualAssistantContext({ isOpen: false });
  }

  const handleStartNewThread = () => {
    setMessages([]);
    setThreadId('');
    setSelectedResponseId(undefined);
    setFeedback('');
    setPrompt('');

    setResponseFeedbackSelectionById({});
    apologizedResponseIdsRef.current = new Set();
    sessionStorage.removeItem(getStorageKey(projectRunConfigurationId));

    const input = document.querySelector<HTMLInputElement>('.ai-chat-panel__input input');
    input?.focus();
  }

  const handleClickThumbsDown = (responseId: number) => {
    setSelectedResponseId(responseId);
    setFeedback('');
  }

  const handleClickThumbsUp = async (responseId: number) => {
    setResponseFeedbackSelectionById(prev => ({ ...prev, [responseId]: AiResponseReviewRating.Good }));

    try {
      await aiResponseReviewService.updateAiResponseReview(responseId, { userRating: AiResponseReviewRating.Good });
      toast("Feedback submitted. Thank you!", {
        containerId: "z-index-9999",
        type: 'success',
      });
    } catch {
      setResponseFeedbackSelectionById(prev => ({ ...prev, [responseId]: undefined }));
      toast("Problem submitting feedback. Please try again.", {
        containerId: "z-index-9999",
        type: 'error',
      });
    }
  }

  const handleSubmitFeedback = async () => {
    if (!selectedResponseId) {
      return;
    }

    const previousRating = responseFeedbackSelectionById[selectedResponseId];
    setResponseFeedbackSelectionById(prev => ({ ...prev, [selectedResponseId]: AiResponseReviewRating.Bad }));

    try {
      await aiResponseReviewService.updateAiResponseReview(selectedResponseId, {
        userRating: AiResponseReviewRating.Bad,
        userReason: feedback
      });
      if (!apologizedResponseIdsRef.current.has(selectedResponseId)) {
        apologizedResponseIdsRef.current.add(selectedResponseId);
        setMessages(prev => [...prev, {
          content: ["Sorry I wasn't able to get that right. " + feedbackTips],
          messageType: AiChatMessageType.ResponseText,
          timestamp: new Date(),
        }]);
      }
      setFeedback('');
      toast("Feedback submitted. Thank you!", {
        containerId: "z-index-9999",
        type: 'success',
      });
    } catch {
      setResponseFeedbackSelectionById(prev => ({ ...prev, [selectedResponseId]: previousRating }));
      toast("Problem submitting feedback. Please try again.", {
        containerId: "z-index-9999",
        type: 'error',
      });
    }
  }

  // We're memoizing Markdown generation to prevent the Markdown component from re-rendering on every keystroke,
  // which has caused performance issues/UI stutters.

  const markdownComponents = useMemo(() => ({
    a: ({ ...props }: React.ComponentPropsWithoutRef<'a'>) =>
      <AIChatPanelProviderLink {...props} />,
    // Wrap tables in a scrollable container instead of setting display:block on the
    // <table> itself, so thead/tbody stay part of one real table (header/body columns
    // stay aligned) while wide tables still scroll horizontally within the panel.
    table: ({ ...props }: React.ComponentPropsWithoutRef<'table'>) =>
      <div className="ai-chat-panel__message__table-wrapper">
        <table {...props} />
      </div>
  }), []);

  const remarkPlugins = useMemo(() => [remarkGfm], []);

  const getMessageMarkup = useCallback((messages: string[], className = "ai-chat-panel__message__response") => {
    const messagesWithProviderUrls = messages.map(message => getCellMarkdownWithCompleteProviderLinks(message));
    return (
      <div className={className}>
        <Markdown
          remarkPlugins={remarkPlugins}
          components={markdownComponents}
        >
          {messagesWithProviderUrls.join('\n')}
        </Markdown>
      </div>
    );
  }, [getCellMarkdownWithCompleteProviderLinks, markdownComponents, remarkPlugins]);

  if (!userCanAccessAIChatPanel(featureFlagContext.enabledFeatureFlags, userContext.roles)) {
    return null;
  }

  rethrowIfUnhandled();

  const renderChartOrTable = (message: AiChatMessage): ReactNode => {
    if (message.messageType === AiChatMessageType.ResponseText || message.messageType === AiChatMessageType.UserPrompt
      || message.messageType === AiChatMessageType.Unknown || !message.content) {
      return <p className="ai-chat-panel__message__response">Sorry, something went wrong. Please try again.</p>;
    }

    if (message.content.length === 0 || message.content[0] === '""') {
      return <p className="ai-chat-panel__message__response">Sorry, the requested table {featureFlagContext.enabledFeatureFlags.has(Features.AI_CHARTING_ENABLED) ? "or chart " : " "}contains no data.</p>;
    }

    if (!featureFlagContext.enabledFeatureFlags.has(Features.AI_CHARTING_ENABLED)) {
      return <AIChatTableChart message={message} threadId={threadId} projectRunConfigurationId={projectRunConfigurationId} />
    }

    switch (message.messageType) {
      case AiChatMessageType.BarChart:
        return <AIChatBarChart message={message} />
      case AiChatMessageType.PieChart:
        return <AIChatPieChart message={message} isFullScreen={isFullScreen} />
      default: //Build a table in the default case
        return <AIChatTableChart message={message} threadId={threadId} projectRunConfigurationId={projectRunConfigurationId} />
    }
  }

  return (
    <div
      className={cx("ai-chat-container", {
        "ai-chat-container--flyout": isFlyout,
        // Every class here has to be derived from props/state. Toggling full screen recomputes this
        // whole string, so a transition class applied imperatively via classList (the way
        // FlyoutContext drives .new-flyout) would be wiped on the next full-screen toggle and strand
        // the panel off-screen. Flyout.tsx gets away with it only because its className never changes.
        "ai-chat-container--flyout-open": isFlyout && isVisible,
        "ai-chat-container--flyout-closed": isFlyout && !isVisible,
        "ai-chat-container--full-screen": isFullScreen,
      })}
      // A hidden flyout is still mounted off-screen; keep it out of the tab order and the a11y tree.
      inert={isFlyout && !isVisible}
    >
      <div className="ai-chat-panel__inner-container">
        <div className="ai-chat-panel__header">
          <div className="ai-chat-panel__header-logo-name">
            <QesIcon iconName={QesIcons.Quinn} description="Quinn" />
            <h3>Ask Quinn</h3>
          </div>
          <div className="ai-chat-panel__header-actions">
            <QESButton
              qesType="secondary"
              qesTestId="ai-chat-panel__full-screen-toggle"
              onClick={() => setIsFullScreen(current => !current)}
            >
              {isFullScreen ? "Exit Full Screen" : "View Full Screen"}
            </QESButton>
            <QESButton
              qesType="secondary"
              qesTestId="ai-chat-panel__close"
              className={isFlyout ? "ai-chat-panel__hide-button" : undefined}
              onClick={handleClose}
              aria-label={isFlyout ? "Hide Quinn" : "Close Quinn"}
            >
              {isFlyout ? (
                // Mirrors the Show/Hide control on the new project dashboard (Accordion variant="flat"):
                // label then chevron, rather than the rail's bare X.
                <span className="ai-chat-panel__hide-button-content">
                  <span className="ai-chat-panel__hide-button-label">Hide</span>
                  <QesIcon iconName={QesIcons.ChevronRight} width="24" isDecorative={true} />
                </span>
              ) : (
                <QesIcon iconName={QesIcons.X} color="black" fontSize="x-large" isDecorative={true} />
              )}
            </QESButton>
          </div>
        </div>
        <div className={cx("ai-chat-panel", { "ai-chat-panel--full-screen": isFullScreen })}>
          {limitReached && !limitsLoading && (
            <p className="ai-chat-panel__limit-warning">Your organization reached the weekly query limit. Free access resets every Monday. Upgrade your plan for unlimited access anytime.</p>
          )}
          <div className="ai-chat-panel__messages">
            {(!messages || messages.length === 0) && (
              <div className="ai-chat-panel__message">
                <div className="ai-chat-panel__message__response ai-chat-panel__message__response--welcome">
                  <p className="ai-chat-panel__message__response-partial">
                    Quinn is your QES Adequacy AI Assistant. How can Quinn help?
                  </p>
                  <QESButton
                    qesType="secondary"
                    disabled={limitReached || limitsLoading}
                    className="ai-chat-panel__welcome-button"
                    qesTestId="ai-chat-panel__welcome-button-1"
                    onClick={() => handleSubmit("Give me out of network alternative provider insights in this project.")}>
                    Out of Network Alternative Insights
                  </QESButton>
                  <QESButton
                    qesType="secondary"
                    disabled={limitReached || limitsLoading}
                    className="ai-chat-panel__welcome-button"
                    qesTestId="ai-chat-panel__welcome-button-1"
                    onClick={() => handleSubmit("Give me insights into physicians in this project.")}>
                    Physician Insights</QESButton>
                  <QESButton
                    qesType="secondary"
                    disabled={limitReached || limitsLoading}
                    className="ai-chat-panel__welcome-button"
                    qesTestId="ai-chat-panel__welcome-button-1"
                    onClick={() => handleSubmit("What questions can I ask?")}>
                    More Ideas</QESButton>
                </div>
              </div>
            )}
            {/* System messages are model-only context (display context injected by
                buildDisplayContextPayload, plus the provider/contract context pushed below). They
                live in `messages` because that is the payload we send, but no branch below renders
                one, so mapping them would emit an empty row that still takes its margin. */}
            {messages && messages.filter(message => message.messageType !== AiChatMessageType.System).map((message, index) => (
              <div className="ai-chat-panel__message" key={index}>
                {(message.dataIsTruncated === true || message.content.length > 51 && message.messageType === AiChatMessageType.Table) && (
                  <div className="ai-chat-panel__message__truncation-warning">
                    <p>
                      {message.messageType === AiChatMessageType.Table ? "This table preview contains only part of the available information:" : "This response was created using only part of the available information:"}
                    </p>
                  </div>
                )}
                {(message.messageType === AiChatMessageType.Unknown || message.messageType === AiChatMessageType.LineChart) &&
                  <div className="ai-chat-panel__message__response">
                    <p>Sorry, I can&apos;t do that yet. Please try again.</p>
                  </div>}
                {message.messageType === AiChatMessageType.UserPrompt && (
                  <p className="ai-chat-panel__message__prompt">{message.content.join('\n')}</p>
                )}
                {(message.messageType === AiChatMessageType.ResponseText) && getMessageMarkup(message.content)}
                {(message.messageType === AiChatMessageType.Table) &&
                  <div className="ai-chat-panel__message__response ai-chat-panel__message__response--table">
                    {renderChartOrTable(message)}
                  </div>}
                {(message.messageType === AiChatMessageType.PieChart || message.messageType === AiChatMessageType.BarChart) &&
                  <div className="ai-chat-panel__message__response ai-chat-panel__message__response--chart">
                    {renderChartOrTable(message)}
                  </div>}
                {!!message.howIFound && (
                  <div className="ai-chat-panel__message__how-i-found">
                    <Collapsible title="How I found this" testId={`ai-chat-panel__how-i-found-${message.responseId}`}>
                      {getMessageMarkup([message.howIFound], "ai-chat-panel__message__how-i-found__body")}
                    </Collapsible>
                  </div>
                )}
                {!!message.responseId && (
                  <div className="ai-chat-panel__message__feedback">
                    <p>Response Feedback:</p>
                    <QESButton
                      aria-label="Thumbs Up"
                      qesTestId={`ai-chat-panel__thumbs-up-${message.responseId}`}
                      qesType="secondary"
                      onClick={() => handleClickThumbsUp(message.responseId)}
                    >
                      <QesIcon
                        className={cx({
                          "ai-chat-panel__thumbs-not-selected": responseFeedbackSelectionById[message.responseId] === 'bad',
                          "ai-chat-panel__thumbs-selected": responseFeedbackSelectionById[message.responseId] === 'good'
                        })}
                        iconName={QesIcons.ThumbsUp}
                        description={responseFeedbackSelectionById[message.responseId] === 'good' ? "positive feedback has been provided for this message" : "provide positive feedback for this message"}
                      />
                    </QESButton>

                    <ModalLink
                      classNames={"qes-button-secondary"}
                      testId={`ai-chat-panel__thumbs-down-${message.responseId}`}
                      content={{
                        body: <QESTextArea label="" hideLabel={true} value={feedback}
                                           testId="ai-chat-panel__feedback" rows={4}
                                           onChange={e => setFeedback(e.target.value)} />,
                        cancelButtonText: "Cancel",
                        confirmButtonAction: () => handleSubmitFeedback(),
                        confirmButtonText: "Submit",
                        title: "Submit Your Feedback",
                      }}>
                      <QesIcon
                        className={cx({
                          "ai-chat-panel__thumbs-not-selected": responseFeedbackSelectionById[message.responseId] === 'good',
                          "ai-chat-panel__thumbs-selected": responseFeedbackSelectionById[message.responseId] === 'bad'
                        })}
                        iconName={QesIcons.ThumbsDown}
                        onClick={() => handleClickThumbsDown(message.responseId)}
                        description={responseFeedbackSelectionById[message.responseId] === 'bad' ? "negative feedback has been provided for this message" : "provide negative feedback for this message"}
                      />
                    </ModalLink>
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="ai-chat-panel__message">
                <div className="ai-chat-panel__message__response">
                  <div className="ai-chat-panel__loading-container">
                    <div className="ai-chat-panel__loading-dot"></div>
                    <div className="ai-chat-panel__loading-dot"></div>
                    <div className="ai-chat-panel__loading-dot"></div>
                    <AIChatLoadingStatuses activeThreadId={effectiveThreadIdRef.current} />
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="ai-chat-panel__prompt">
            <QESTextInput label=""
              qesRef={inputRef}
              className="ai-chat-panel__input"
              testId="ai-chat-panel__input"
              value={prompt}
              placeholder="Tell us to..."
              maxLength={1000}
              onChange={e => setPrompt(e.target.value)}
              hideLabel={true}
              disabled={limitReached}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit().then();
                }
              }}
              inputPrepend={<QesIcon iconName={QesIcons.SearchSm} className="h-100" isDecorative />} />
            <p>
              Quinn is an AI assistant. Responses are AI-generated and may contain errors or inaccuracies. <br />
              You should independently verify all information before relying on it.
              Quinn does not provide professional advice and is not a substitute for human judgment or expertise.
            </p>
            <div>
              <p className="ai-chat-panel__limit-warning-percentage">{warningText}</p>
              {featureFlagContext.enabledFeatureFlags.has(Features.AI_MARKETO_FORM) && showLimitRequestInfo && !limitsLoading && <QuinnLimitRequestInfo />}
            </div>
            <div className="ai-chat-panel__prompt-actions">
              <QESButton qesType="secondary" className="ai-chat-panel__welcome-button"
                qesTestId="ai-chat-panel__clear-history" onClick={handleStartNewThread}>
                Clear Chat History
              </QESButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AIChatPanel;